import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectSpread from "@babel/runtime/helpers/esm/objectSpread";
import { createElement } from "@wordpress/element";
/**
 * WordPress dependencies
 */

import { createContext, useContext, useCallback } from '@wordpress/element';
import { useSelect, useDispatch } from '@wordpress/data';
/**
 * Internal dependencies
 */

import { defaultEntities, kinds } from './entities';

var entities = _objectSpread({}, defaultEntities.reduce(function (acc, entity) {
  if (!acc[entity.kind]) {
    acc[entity.kind] = {};
  }

  acc[entity.kind][entity.name] = {
    context: createContext()
  };
  return acc;
}, {}), kinds.reduce(function (acc, kind) {
  acc[kind.name] = {};
  return acc;
}, {}));

var getEntity = function getEntity(kind, type) {
  if (!entities[kind]) {
    throw new Error("Missing entity config for kind: ".concat(kind, "."));
  }

  if (!entities[kind][type]) {
    entities[kind][type] = {
      context: createContext()
    };
  }

  return entities[kind][type];
};
/**
 * Context provider component for providing
 * an entity for a specific entity type.
 *
 * @param {Object} props          The component's props.
 * @param {string} props.kind     The entity kind.
 * @param {string} props.type     The entity type.
 * @param {number} props.id       The entity ID.
 * @param {*}      props.children The children to wrap.
 *
 * @return {Object} The provided children, wrapped with
 *                   the entity's context provider.
 */


export default function EntityProvider(_ref) {
  var kind = _ref.kind,
      type = _ref.type,
      id = _ref.id,
      children = _ref.children;
  var Provider = getEntity(kind, type).context.Provider;
  return createElement(Provider, {
    value: id
  }, children);
}
/**
 * Hook that returns the ID for the nearest
 * provided entity of the specified type.
 *
 * @param {string} kind The entity kind.
 * @param {string} type The entity type.
 */

export function useEntityId(kind, type) {
  return useContext(getEntity(kind, type).context);
}
/**
 * Hook that returns the value and a setter for the
 * specified property of the nearest provided
 * entity of the specified type.
 *
 * @param {string} kind The entity kind.
 * @param {string} type The entity type.
 * @param {string} prop The property name.
 *
 * @return {[*, Function]} A tuple where the first item is the
 *                          property value and the second is the
 *                          setter.
 */

export function useEntityProp(kind, type, prop) {
  var id = useEntityId(kind, type);
  var value = useSelect(function (select) {
    var _select = select('core'),
        getEntityRecord = _select.getEntityRecord,
        getEditedEntityRecord = _select.getEditedEntityRecord;

    getEntityRecord(kind, type, id); // Trigger resolver.

    var entity = getEditedEntityRecord(kind, type, id);
    return entity && entity[prop];
  }, [kind, type, id, prop]);

  var _useDispatch = useDispatch('core'),
      editEntityRecord = _useDispatch.editEntityRecord;

  var setValue = useCallback(function (newValue) {
    editEntityRecord(kind, type, id, _defineProperty({}, prop, newValue));
  }, [kind, type, id, prop]);
  return [value, setValue];
}
/**
 * Hook that returns whether the nearest provided
 * entity of the specified type is dirty, saving,
 * and a function to save it.
 *
 * The last, optional parameter is for scoping the
 * selection to a single property or a list properties.
 *
 * By default, dirtyness detection and saving considers
 * and handles all properties of an entity, but this
 * last parameter lets you scope it to a single property
 * or a list of properties for each instance of this hook.
 *
 * @param {string}          kind    The entity kind.
 * @param {string}          type    The entity type.
 * @param {string|[string]} [props] The property name or list of property names.
 */

export function __experimentalUseEntitySaving(kind, type, props) {
  var id = useEntityId(kind, type);

  var _useSelect = useSelect(function (select) {
    var _select2 = select('core'),
        getEntityRecordNonTransientEdits = _select2.getEntityRecordNonTransientEdits,
        isSavingEntityRecord = _select2.isSavingEntityRecord;

    var _edits = getEntityRecordNonTransientEdits(kind, type, id);

    var editKeys = Object.keys(_edits);
    return [props ? editKeys.some(function (key) {
      return typeof props === 'string' ? key === props : props.includes(key);
    }) : editKeys.length > 0, isSavingEntityRecord(kind, type, id), _edits];
  }, [kind, type, id, props]),
      _useSelect2 = _slicedToArray(_useSelect, 3),
      isDirty = _useSelect2[0],
      isSaving = _useSelect2[1],
      edits = _useSelect2[2];

  var _useDispatch2 = useDispatch('core'),
      saveEntityRecord = _useDispatch2.saveEntityRecord;

  var save = useCallback(function () {
    var filteredEdits = edits;

    if (typeof props === 'string') {
      filteredEdits = _defineProperty({}, props, filteredEdits[props]);
    } else if (props) {
      filteredEdits = filteredEdits.reduce(function (acc, key) {
        if (props.includes(key)) {
          acc[key] = filteredEdits[key];
        }

        return acc;
      }, {});
    }

    saveEntityRecord(kind, type, _objectSpread({
      id: id
    }, filteredEdits));
  }, [kind, type, id, props, edits]);
  return [isDirty, isSaving, save];
}